import { ROUTES, WEBVIEW_ROUTES } from '@/shared/routes';

import type { THostPage } from '../protocol';

/**
 * Центральная конфигурация моста: всё управление в одном месте.
 * Здесь — какие сервисы существуют, по каким URL/origin они грузятся в iframe,
 * и базовые параметры транспорта. Транспорт строго cross-origin через postMessage.
 */

/** Идентификаторы webview-сервисов (совпадают с маршрутами оболочки). */
export const SERVICE_IDS = {
  autoCredit: 'auto-credit',
  leasing: 'leasing',
  vehicleManagement: 'vehicle-management'
} as const;

export type TServiceId = (typeof SERVICE_IDS)[keyof typeof SERVICE_IDS];

/** Сырые URL сервисов из окружения (Rsbuild прокидывает PUBLIC_*). Пусто, если не задан. */
const SERVICE_URLS: Record<TServiceId, string> = {
  [SERVICE_IDS.autoCredit]: process.env.PUBLIC_AUTO_CREDIT_URL ?? '',
  [SERVICE_IDS.leasing]: process.env.PUBLIC_LEASING_URL ?? '',
  [SERVICE_IDS.vehicleManagement]:
    process.env.PUBLIC_MONI_VEHICLE_MANAGEMENT_URL ?? ''
};

/** clientId сервиса в IdP — для SSO-авторизации (`/sso/authorize`). Пусто, если не задан. */
const SERVICE_CLIENT_IDS: Record<TServiceId, string> = {
  [SERVICE_IDS.autoCredit]: process.env.PUBLIC_AUTO_CREDIT_CLIENT_ID ?? '',
  [SERVICE_IDS.leasing]: process.env.PUBLIC_LEASING_CLIENT_ID ?? '',
  [SERVICE_IDS.vehicleManagement]:
    process.env.PUBLIC_MONI_VEHICLE_MANAGEMENT_CLIENT_ID ?? ''
};

/** Маршрут оболочки, под которым монтируется сервис (splat: `${route}/*`). */
export const SERVICE_ROUTES: Record<TServiceId, string> = {
  [SERVICE_IDS.autoCredit]: WEBVIEW_ROUTES.autoCredit,
  [SERVICE_IDS.leasing]: WEBVIEW_ROUTES.leasing,
  [SERVICE_IDS.vehicleManagement]: WEBVIEW_ROUTES.vehicleManagement
};

/**
 * Страницы оболочки за смысловыми id `navigation.openHostPage` (без языка и ведущего слэша,
 * как в `ROUTES`). Нативка держит такую же таблицу на свои экраны.
 */
export const HOST_PAGE_ROUTES: Record<THostPage, string> = {
  support: ROUTES.supportContact,
  supportRequests: ROUTES.supportRequests,
  supportRequestCreate: ROUTES.supportRequestCreate
};

/** Таймаут ожидания RPC-ответа от сервиса (мс). */
export const RPC_TIMEOUT_MS = 15_000;

/** Origin оболочки — сервис должен слать сообщения только сюда (проверяется адаптером сервиса). */
export const HOST_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : '';

export interface IServiceBridgeConfig {
  id: TServiceId;
  /** Базовый URL сервиса (origin + опциональный путь). */
  url: string;
  /** Origin сервиса — целевой для postMessage и allowlist входящих сообщений. */
  origin: string;
  /**
   * Базовый внутренний путь сервиса (pathname из url) — его iframe уже грузит в src.
   * Нужен, чтобы НЕ слать initialPath при входе на базовый путь (паритет с нативкой,
   * где route.params.initialPath обычно undefined и сервис при старте не навигирует).
   */
  basePath: string;
  /** clientId сервиса в IdP (для SSO). */
  clientId: string;
}

function safeBasePath(url: string): string {
  if (!url) return '/';
  try {
    return new URL(url).pathname.replace(/\/+$/, '') || '/';
  } catch {
    return '/';
  }
}

function safeOrigin(url: string): string {
  if (!url) return '';
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

/** Конфиг конкретного сервиса (URL + origin + clientId). */
export function getServiceConfig(id: TServiceId): IServiceBridgeConfig {
  const url = SERVICE_URLS[id] ?? '';
  return {
    id,
    url,
    origin: safeOrigin(url),
    basePath: safeBasePath(url),
    clientId: SERVICE_CLIENT_IDS[id] ?? ''
  };
}

/**
 * Собирает src для iframe с локалью и стартовым внутренним маршрутом.
 * Локаль вставляется сразу после origin (как нативный buildSourceUrl), затем — initialPath.
 * Вызывается ОДИН раз на маунте: src фиксируется и больше не меняется (iframe не перезагружается).
 */
export function buildServiceUrl(
  baseUrl: string,
  locale: string,
  initialPath = ''
): string {
  if (!baseUrl) return '';

  const path = initialPath.replace(/^\/+/, '');

  try {
    const url = new URL(baseUrl);
    const basePath = url.pathname.replace(/\/+$/, '');
    const segments = [locale, basePath.replace(/^\/+/, ''), path]
      .filter(Boolean)
      .join('/');
    url.pathname = `/${segments}`;
    return url.toString();
  } catch {
    return baseUrl;
  }
}
